/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import { CrossIconContainer, StyledDialogTitle } from "../audit_log_modal/styles";
import ClearIcon from '@mui/icons-material/Clear';
import camelize from "../../utils/camelize";
import { IndexTable } from "../index_table";
import { MODAL_TABLE_MAX_HEIGHT } from "../index_table/styles";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import api from "../../api";
import { OrderAppliedDiscountModal } from "../order_discount_modal";
import { OrderNumberCell } from "../order_number_cell";

export const EmployeePastVisitsModal = ({ open, handleClose, data }) => {
  const [selectedOrder, setSelectedOrder] = useState('')
  const [orderOpen, setOrderOpen] = useState('')

  const fetchOrderAppliedDiscount = async () => {
    const response = await api.get(`/v1/admin/orders/${selectedOrder}/detail`)
    return response?.data
  }

  const { data: orderData, isLoading: orderLoading } = useQuery({
    queryKey: ['insight_order_discount_summary', selectedOrder],
    queryFn: fetchOrderAppliedDiscount,
    staleTime: 5 * 60 * 1000,
    enabled: selectedOrder !== ''
  })

  const handleOrderOpen = (order_id) => {
    setOrderOpen(true)
    setSelectedOrder(order_id)
  }

  const handleOrderClose = () => {
    setOrderOpen(false)
  }

  const tableColumns = [
    'restaurant_id',
    'restaurant_name',
    'check_in_date',
    'order_#',
    'order_details',
    'total_order_amount_before_discount',
    'discount',
    'total_order_amount_after_discount',
  ];

  const columns = tableColumns?.map((column) => {
    let customLabel = camelize(column);
    if(column === 'total_order_amount_before_discount') customLabel = 'Total Order Amount Before Perq';
    if(column === 'discount') customLabel = 'Perq';
    if(column === 'total_order_amount_after_discount') customLabel = 'Total Order Amount After Perq';
    return {
      key: column,
      label: customLabel,
      sortable: column !== 'order_details'
    };
  });

  const TableRows = (data) => {
    return data.past_visits?.map((record) => {
      return {
        restaurant_id: record?.id,
        restaurant_name: record?.name,
        check_in_date: record?.date && moment.utc(record?.date).format("MM/DD/YYYY"),
        'order_#': record?.order_no,
        'order_details': (
          <OrderNumberCell
            orderValue={record?.order_id}
            onView={handleOrderOpen}
          />
        ),
        total_order_amount_before_discount: `$${Number(record?.total_order_amount_before_discount).toFixed(2) ?? '0.00'}`,
        discount: `$${Number(record?.discount).toFixed(2) ?? '0.00'}`,
        total_order_amount_after_discount: `$${Number(record?.total_order_amount_after_discount).toFixed(2) ?? '0.00'}`,
        order_id: record?.order_id
      };
    });
  };

  const rows = data.past_visits ? TableRows(data) : [];

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth={"lg"}
      >
        <CrossIconContainer>
          <ClearIcon onClick={handleClose} />
        </CrossIconContainer>
        <StyledDialogTitle><span>{data?.name}'s</span> Past Visits:</StyledDialogTitle>
        <DialogContent
          sx={{
            overflowY: 'scroll',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' }
          }}
        >
          <IndexTable
            maxHeight={MODAL_TABLE_MAX_HEIGHT}
            columns={columns}
            data={rows || []}
            tableId="employee-past-visits"
            type="entries"
            itemsPerPage={10}
            pageCount={4}
            page={1}
            report={false}
            hideNavigation={true}
            handleOpen={handleOrderOpen}
          />
        </DialogContent>
      </Dialog>

      {/* Order Detail Modal */}
        <OrderAppliedDiscountModal
          open={orderOpen}
          handleClose={handleOrderClose}
          ordersData={orderData}
          isLoading={orderLoading}
        />
    </React.Fragment>
  )
}
